import { type TemplateField } from '@/components/features/ServiceRequests/DynamicRequestForm';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { route } from 'ziggy-js';

interface ShowTemplatePageProps extends PageProps {
    [key: string]: unknown;
    template: {
        id: string;
        slug: string;
        name: string;
        description: string | null;
        icon: string | null;
        category: string | null;
        is_active: boolean;
        sort_order: number;
        fields: TemplateField[];
        created_at: string | null;
        updated_at: string | null;
    };
}

export default function AdminServiceRequestTemplateShowPage() {
    const { props } = usePage<ShowTemplatePageProps>();
    const { template } = props;
    const { t } = useTranslations();

    const breadcrumbs = [
        { title: t('service_requests.admin_queue_title', 'Service Request Queue'), href: '/admin/service-requests' },
        { title: t('service_requests.template_manager_title', 'Request Templates'), href: '/admin/service-request-templates' },
        { title: template.name, href: '#' },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={template.name} />

            <PageHeader
                title={template.name}
                subtitle={template.description ?? ''}
                actions={
                    <Button asChild>
                        <Link href={route('admin.service-request-templates.edit', template.id)}>
                            {t('service_requests.edit_template', 'Edit Template')}
                        </Link>
                    </Button>
                }
            />

            <PageContent className="space-y-5">
                <div className="bg-card rounded-xl border p-5">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <Badge
                            variant="outline"
                            className={template.is_active ? 'bg-success-subtle text-success-subtle-foreground' : 'bg-muted text-muted-foreground'}
                        >
                            {template.is_active
                                ? t('service_requests.template_active', 'Active')
                                : t('service_requests.template_inactive', 'Inactive')}
                        </Badge>
                        {template.category && <Badge variant="outline">{template.category}</Badge>}
                    </div>
                    <dl className="divide-y text-sm">
                        {[
                            ['Slug', template.slug],
                            ['Icon', template.icon ?? '—'],
                            ['Sort order', String(template.sort_order)],
                            ['Created', template.created_at ? new Date(template.created_at).toLocaleString() : '—'],
                            ['Updated', template.updated_at ? new Date(template.updated_at).toLocaleString() : '—'],
                        ].map(([label, value]) => (
                            <div key={label} className="grid grid-cols-[140px_1fr] gap-2 py-2.5">
                                <dt className="text-muted-foreground">{label}</dt>
                                <dd className="font-medium">{value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className="bg-card rounded-xl border p-5">
                    <h2 className="mb-4 text-sm font-semibold tracking-wide uppercase">
                        {t('service_requests.template_fields', 'Fields')} ({template.fields.length})
                    </h2>
                    {template.fields.length === 0 ? (
                        <p className="text-muted-foreground text-sm">No fields defined.</p>
                    ) : (
                        <div className="space-y-2">
                            {[...template.fields]
                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                .map((field) => (
                                    <div key={field.key} className="rounded-lg border p-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm font-medium">{field.label}</p>
                                            <div className="flex items-center gap-1.5">
                                                <Badge variant="outline" className="text-xs">
                                                    {field.type}
                                                </Badge>
                                                {field.required && (
                                                    <Badge variant="outline" className="bg-danger-subtle text-danger-subtle-foreground text-xs">
                                                        required
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-muted-foreground mt-0.5 text-xs">key: {field.key}</p>
                                        {field.help_text && <p className="text-muted-foreground mt-1 text-xs">{field.help_text}</p>}
                                        {field.options && field.options.length > 0 && (
                                            <p className="text-muted-foreground mt-1 text-xs">Options: {field.options.join(', ')}</p>
                                        )}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </PageContent>
        </AppLayout>
    );
}
